import React, { useState, useRef } from "react";
import "./createLegalDoc.css";
import { logEvent } from "firebase/analytics";
import { config } from "../config";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import FullScreenLoader from "../components/FullScreenLoader";
import GoogleButton from "../components/GoogleAuth"; 
import { Helmet } from "react-helmet-async";
import LeftPanel from "../components/leftPanel";
import { analytics } from "../components/firebase";
const LeaseAgreement = () => {
  const googleAuthRef = useRef(null);
  const [landsLordName, setLandsLordName] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [leaseStartDate, setLeaseStartDate] = useState("");
  const [rentAmount, setRentAmount] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [maintenance, setMaintenance] = useState("");
  const [termination, setTermination] = useState("");
  const [language, setLanguage] = useState("English");
  const [errors, setErrors] = useState({});
  const [popupMessage] = useState("");
  const [isPopupVisible] = useState(false);
  const textAreaRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(true);

  const navigate = useNavigate();
  React.useEffect(() => {
    document.documentElement.style.setProperty("--main-color", config.color);
    logEvent(analytics, "page_view", { page_name: "LeaseAgreement" });
  }, [config.color]);

  console.log("✅ Create page loaded!");
  const location = useLocation();

  const initialText = location.state?.text || "";
  const receivedKey = location.state?.key || "Legal Document";
  const [Topic, setTopic] = useState(initialText);
  const [Keypoints, setKeypoints] = useState("");

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setIsButtonVisible(false);
    document.body.classList.add("no-scroll");

    try {
      const requestBody = {
        landsLordName,
        tenantName,
        propertyAddress,
        leaseStartDate,
        rentAmount,
        depositAmount,
        maintenance,
        termination,
        selectedLanguage: language,
      };

      const generationResponse = await fetch(
        `${config.baseURL}/lease_gen.php`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );

      const rawResponse = await generationResponse.text();
      let data;

      try {
        data = JSON.parse(rawResponse);
      } catch {
        toast.error("Please re-try after some time");
        return;
      }

      if (data.error) {
        if (data.error === "Authentication failed") {
         
          googleAuthRef.current?.login();
          return;
        }
        if (data.redirect === "premium") {
          navigate(`/premium?id=${data.premiumId}`);
          return;
        }
        toast.error("Please re-try after some time");
        return;
      }

      if (data.success && data.resultText) {
        logEvent(analytics, "form_submission_success", {
          form_name: "LeaseAgreement",
          result: "success",
        });
        navigate("/result", {
          state: { result: data.resultText, history_id: data.history_id },
        });
        setLandsLordName("");
        setTenantName("");
        setPropertyAddress("");
        setLeaseStartDate("");
        setRentAmount("");
        setDepositAmount("");
        setMaintenance("");
        setTermination("");
        setLanguage("English");
      } else {
        toast.error("Please re-try after some time");
        logEvent(analytics, "form_submission_failure", {
          form_name: "LeaseAgreement",
          error: "Form submission failed",
        });
      }
    } catch (error) {
      toast.error("Please re-try after some time");
      logEvent(analytics, "error", {
        error_type: "api_call_failed",
        error_message: "Failed to generate document due to API error.",
        action: "generate_button_click",
      });
    } finally {
      setIsLoading(false);
      document.body.classList.remove("no-scroll");
    }
  };

  const handleLoginSuccess = () => {
    toast.success(
      "Welcome back! Your inputs have been restored. Click Generate to continue."
    );
  };

  const handleLoginFailure = () => {};

  return (
    <>
      <Helmet key="landing">
        <title>
          Lease Agreement Generator | AI-Powered Legal Lease Templates Online
        </title>

        <meta
          name="description"
          content="Create legally sound lease agreements in minutes with our AI-powered lease generator. Customize rental terms, landlord and tenant details, rent, deposit, and more. Fast, easy, and secure."
        />

        <meta
          name="keywords"
          content="lease agreement generator, rental contract template, AI lease form, create lease online, landlord tenant agreement, customizable lease template, online rental agreement, house lease document, apartment lease form, rent agreement creator, residential lease generator, legal lease contract, rental lease automation, free lease form, tenancy agreement generator, AI rental contract, make lease documents, landlord rental form, real estate lease, rent contract online"
        />

        <link
          rel="canonical"
          href="https://legaldocumentgenerator.com/lease-agreement-templates"
        />
      </Helmet>
      <div className="create_background">
        {isLoading && <FullScreenLoader />}
        {isPopupVisible && (
          <div className="popup-notification">
            <p>{popupMessage}</p>
          </div>
        )}

        <LeftPanel />
        <div className="create_main_templates">
           <div className="create_title">
                     <img src={config.Backbutton} alt="back" onClick={handleBackClick} />
                     Create AI Legal Document
                   </div>
          <div className="create_form_grid">
            <div className="create_inputbox1">
              Landlord's name and details
              <textarea
                className={`textarea_input ${
                  errors.landsLordName ? "error" : ""
                }`}
                placeholder="e.g, John Smith, contact: (123) 456-7890 , email:johnsmith@gmail.com"
                value={landsLordName}
                onChange={(e) => setLandsLordName(e.target.value)}
                maxLength={1000}
              />
              {errors.landsLordName && (
                <div className="error-message">{errors.landsLordName}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Tenant's full name and details
              <textarea
                className={`textarea_input ${errors.tenantName ? "error" : ""}`}
                placeholder="e.g, Jane Doe, contact: (987) 654-3290 , email:janedoe@gmail.com"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                maxLength={1000}
              />
              {errors.tenantName && (
                <div className="error-message">{errors.tenantName}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Property address
              <textarea
                className={`textarea_input ${
                  errors.propertyAddress ? "error" : ""
                }`}
                placeholder="e.g, 123 Main Street, Apartment 4B, Springfield, IL"
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                maxLength={1000}
              />
              {errors.propertyAddress && (
                <div className="error-message">{errors.propertyAddress}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Lease start and end date
              <textarea
                className={`textarea_input ${
                  errors.leaseStartDate ? "error" : ""
                }`}
                placeholder="e.g, Start: January 1, 2024, End: December 1, 2024"
                value={leaseStartDate}
                onChange={(e) => setLeaseStartDate(e.target.value)}
                maxLength={1000}
              />
              {errors.leaseStartDate && (
                <div className="error-message">{errors.leaseStartDate}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Rent amount and payment terms
              <textarea
                className={`textarea_input ${errors.rentAmount ? "error" : ""}`}
                placeholder="e.g, $1,200 per month, due on the 1st"
                value={rentAmount}
                onChange={(e) => setRentAmount(e.target.value)}
                maxLength={1000}
              />
              {errors.rentAmount && (
                <div className="error-message">{errors.rentAmount}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Security deposit amount
              <textarea
                className={`textarea_input ${
                  errors.depositAmount ? "error" : ""
                }`}
                placeholder="e.g, $2400 (equivalent to 2 month's rent)"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                maxLength={1000}
              />
              {errors.depositAmount && (
                <div className="error-message">{errors.depositAmount}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Maintenance responsibilities
              <textarea
                className={`textarea_input ${
                  errors.maintenance ? "error" : ""
                }`}
                placeholder="e.g, Tenant responsible for minor repairs"
                value={maintenance}
                onChange={(e) => setMaintenance(e.target.value)}
                maxLength={1000}
              />
              {errors.maintenance && (
                <div className="error-message">{errors.maintenance}</div>
              )}
            </div>

            <div className="create_inputbox1">
              Termination notice requirements
              <textarea
                className={`textarea_input ${
                  errors.termination ? "error" : ""
                }`}
                placeholder="e.g, 30 days written notice required"
                value={termination}
                onChange={(e) => setTermination(e.target.value)}
                maxLength={1000}
              />
              {errors.termination && (
                <div className="error-message">{errors.termination}</div>
              )}
            </div>

            <div>
              <div className="create_inputbox1">Select Language</div>
              <select
                className="text_input"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="English">English</option>
                <option value="Arabic">Arabic (RTL)</option>
                <option value="Bulgarian">Bulgarian</option>
                <option value="Chinese">Chinese (Simplified)</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Hebrew">Hebrew (RTL)</option>
                <option value="Hindi">Hindi</option>
                <option value="Indonesian">Indonesian</option>
                <option value="Italian">Italian</option>
                <option value="Japanese">Japanese</option>
                <option value="Portuguese">Portuguese</option>
                <option value="Punjabi">Punjabi</option>
                <option value="Russian">Russian</option>
                <option value="Spanish">Spanish</option>
                <option value="Swahili">Swahili</option>
                <option value="Tagalog">Tagalog</option>
                <option value="Urdu">Urdu</option>
              </select>
            </div>
          </div>
          <ToastContainer />
          <GoogleButton
            id="loginHidden"
            ref={googleAuthRef}
            onLoginSuccess={handleLoginSuccess}
            onLoginFailure={handleLoginFailure}
          />
          <button
            className="generate_btn"
            alt="Generate"
            onClick={handleGenerate}
          >
            Continue
          </button>
        </div>
      </div>
    </>
  );
};

export default LeaseAgreement;
